import Grid2 from "@mui/material/Unstable_Grid2";
import {Box, Card, Divider, IconButton, Skeleton, SvgIcon, TextField, Tooltip, Typography} from "@mui/material";
import React, {useCallback, useEffect, useMemo} from "react";
import Chips, {ChipsTypes} from "../../../../../components/base/chips/chips";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {usePartnerInformation} from "../../../../../hooks/use-partner-information";
import ToastService from "../../../../../../core/services/toast";
import Api from "../../../../../../core/services/api/api";
import {ConfigurableInfoBlockInputTypes} from "../../../../../components/app-specific/configurable-info-block";
import {AutoModeRounded} from "@mui/icons-material";
import {ReactComponent as ToggleIcon} from "../../../../../../assets/images/warranty-statuses-reasons/toggle.svg";
import classnames from "classnames";
import DateUtils from "../../../../../../core/services/date-utils";
import Form, {MuiRFF, Yup} from "../../../../../components/form";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import Button from "../../../../../components/base/button/button";
import {createUUId} from "../../../../../../core/services/utils/utils";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {Collapse} from "reactstrap";


const PartnerManagementView = () => {
    const [adminAuthToken, setAdminAuthToken] = React.useState("");
    const [partners, setPartners] = React.useState([]);
    const [opened, setOpened] = React.useState(false);
    const [fetchingPartnerInformation, setFetchingPartnerInformation] = React.useState({});
    const [partnerDetails, setPartnerDetails] = React.useState({})
    const isMounted = useIsMounted();

    const currentPartner = usePartnerInformation();

    const {configureNotebookTags} = useConfigureNotebook('partners-management-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.settingsPartnersManagement);
        configureBehavioralTool((tags) => tags.settingsPartnersManagement);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Pre-loads the current partner information as one of the partners in the list.
     */
    React.useEffect(() => {
        if (!currentPartner) {
            return;
        }
        const partnerId = currentPartner.id;
        setPartners((prevState) => {
            const index = prevState.findIndex(e => e.id === partnerId);
            if (index !== -1) {
                return prevState.map((e, i) => i === index ? currentPartner : e);
            } else {
                return [currentPartner, ...prevState];
            }
        });
        return () => {
            setPartners(prevState => prevState.filter(e => e.id !== partnerId));
        }
    }, [currentPartner]);

    /**
     * Fetches the partner information for the given partner id (this is different from fetching current partner information.
     * @type {(function(*, boolean=): Promise<*>)|*}
     */
    const fetchPartnerSetupChecklist = React.useCallback(async (partnerId) => {
        if (!adminAuthToken?.trim()) {
            ToastService.warning({
                title: "Admin Auth Token Not Found",
                message: "Please Ensure that the admin auth token is set before fetching partner services."
            })
            return false;
        }
        setFetchingPartnerInformation(prevState => ({
            ...prevState,
            [partnerId]: true,
        }));
        const response = await Api.getPartnerSetupCheckList({
            partnerId: partnerId,
            authToken: adminAuthToken,
        });
        if (!isMounted())
            return;
        setFetchingPartnerInformation(prevState => ({
            ...prevState,
            [partnerId]: false,
        }));
        if (response?.resultFlag) {
            setPartnerDetails(prevState => ({
                ...prevState,
                [partnerId]: {
                    ...(prevState[partnerId] ?? {}),
                    setupChecklist: response.data,
                },
            }))
        }
        return response?.resultFlag;
    }, [adminAuthToken, isMounted])

    /**
     *
     * @type {(function(*): Promise<void>)|*}
     */
    const setOpenedForPartner = React.useCallback(async (partnerId) => {
        const toggleIsOpened = () => new Promise((resolve) => {
            setOpened(prevState => {
                const newState = ({
                    ...prevState,
                    [partnerId]: !prevState[partnerId],
                })
                resolve(newState[partnerId]);
                return newState;
            })
        })
        const isOpened = await toggleIsOpened();
        if (!isMounted())
            return;
        if (isOpened) {
            const resultFlag = await fetchPartnerSetupChecklist(partnerId);
            if (!resultFlag) {
                // we just reverse it again.
                void toggleIsOpened();
            }
        }
    }, [fetchPartnerSetupChecklist, isMounted])

    /**
     * Adds the partner into the list of partners and fetches its current services.
     * @type {(function(PartnerInformation): Promise<*>)|*}
     */
    const onNewPartnerCreated = React.useCallback(async (partner) => {
        setPartners((prevState) => {
            const index = prevState.findIndex(e => e.id === partner.id);
            if (index !== -1) {
                return prevState.map((e, i) => i === index ? partner : e);
            } else {
                return [...prevState, partner];
            }
        });
        await fetchPartnerSetupChecklist(partner.id);
    }, [fetchPartnerSetupChecklist]);


    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
                <Card variant={'outlined'} sx={{padding: 2}}>
                    <Grid2 container spacing={3}>
                        <Grid2 xs={12}>
                            <Typography variant={'h6'} fontWeight={600}>
                                Partner Management
                            </Typography>
                        </Grid2>
                        <Grid2 xs={12}>
                            <TextField
                                label={"Admin Auth Token"}
                                inputType={ConfigurableInfoBlockInputTypes.input}
                                value={adminAuthToken}
                                onChange={(e) => setAdminAuthToken(e.target.value)}
                                fullWidth
                            />
                        </Grid2>
                        <Grid2 xs={12}>
                            <Typography variant={'body2'}>
                                Select from the list of partners to view their setup-checklist.
                            </Typography>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                                {
                                    partners.map(partner => (
                                        <PartnerCard
                                            key={partner.id}
                                            partner={partner}
                                            opened={opened[partner.id]}
                                            setOpened={setOpenedForPartner}
                                            loading={fetchingPartnerInformation[partner.id] ?? false}
                                            details={partnerDetails[partner.id] ?? {}}
                                        />
                                    ))
                                }
                            </Box>
                        </Grid2>
                    </Grid2>
                </Card>
                <CreatePartnerForm
                    adminAuthToken={adminAuthToken}
                    onCreate={onNewPartnerCreated}
                />
            </Box>
        </>
    );
}


const PartnerCard = ({partner, opened, setOpened, loading, details}) => {
    const currentPartner = usePartnerInformation();
    const isCurrentPartner = currentPartner?.id === partner.id && !!partner.id;

    return (
        <Card variant={'outlined'} sx={{padding: 1, backgroundColor: 'background.default'}}>
            <Grid2 container spacing={2}>
                <Grid2 xs={12} sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}} onClick={() => setOpened(partner.id)}>
                    <Box sx={{display: 'flex', flexDirection: 'column'}}>
                        <Typography variant={'subtitle1'} fontWeight={600}>
                            {partner.name ?? ""} <Typography variant={'caption'} fontWeight={600} color={'tertiary.main'} sx={{ml: 0.5}}>{isCurrentPartner && "(Current Partner)"}</Typography>
                        </Typography>
                        <Typography variant={'caption'} fontWeight={600}>
                            {partner.id?.toUpperCase() ?? ""}
                        </Typography>
                    </Box>
                    <IconButton
                        className={'toggle-button'}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpened(partner.id)
                        }}
                    >
                        <SvgIcon component={ToggleIcon} className={classnames('icon normal toggle-icon', {'open': opened})}/>
                    </IconButton>
                </Grid2>
                <Grid2 xs={12}>
                    <Collapse in={opened} timeout="auto">
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1,}}>
                                {
                                    loading &&
                                    Array({length: 5}).map((_, index) => (
                                        <Skeleton key={index} width={100} height={40} sx={{transform: 'none', borderRadius: 2,}}/>
                                    ))
                                }
                                {
                                    !loading &&
                                    details?.setupChecklist?.map(service => (
                                        <Tooltip key={service.id} title={service.comment ?? ""}>
                                            <Chips
                                                key={service.id}
                                                type={ChipsTypes.none}
                                                content={`${service.name} | ${service.source} (${DateUtils.UTCToLocal(service.submitDateTimeUTC).format(DateUtils.defaultFormat)})`}
                                            />
                                        </Tooltip>
                                    ))
                                }
                            </Box>
                        </Box>
                    </Collapse>
                </Grid2>
            </Grid2>
        </Card>
    )


}


const CreatePartnerForm = ({adminAuthToken, onCreate}) => {
    const [submitting, setSubmitting] = React.useState(false);
    const isMounted = useIsMounted();

    const schema = useMemo(() => Yup.object().shape({
        partnerId: Yup.string().trim().required(),
        name: Yup.string().trim().required(),
        phone: Yup.string().trim().required().phoneNo(ValidateMessages.incorrectType("phone"), 10),
        fax: Yup.string().trim().required(),
        country: Yup.string().trim().required(),
        province: Yup.string().trim().required(),
        city: Yup.string().trim().required(),
        postalCode: Yup.string().trim().required(),
        fullAddress: Yup.string().trim().required(),
        logo: Yup.string().trim().required(),
        adminId: Yup.string().trim().required(),
        adminEmail: Yup.string().trim().required().email(ValidateMessages.incorrectType("email")),
        description: Yup.string().trim().nullable(),
    }), [])

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Creates a new partner with the given values.
     * @type {(function(*): Promise<void>)|*}
     */
    const submitForm = useCallback(async (values) => {
        if (!adminAuthToken?.trim()) {
            ToastService.warning({
                title: "Admin Auth Token Not Found",
                message: "Please Ensure that the admin auth token is set before creating a new partner."
            })
            return;
        }
        setSubmitting(true);
        const response = await Api.addNewPartner({authToken: adminAuthToken, ...values});
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onCreate(values);
        }
    }, [isMounted, onCreate, adminAuthToken])

    return (
        <>
            <Card variant={'outlined'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                <Grid2 container spacing={2}>
                    <Grid2 xs={12}>
                        <Typography variant={'subtitle1'} fontWeight={600}>
                            Create a new partner
                        </Typography>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Form
                            onSubmit={submitForm}
                            validate={validate}
                            initialValues={{}}
                        >
                            {({form}) => {
                                return (
                                    <Grid2 spacing={2} container>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'partnerId'}
                                                label={'Partner ID'}
                                                required={required.partnerId}
                                                disabled={submitting}
                                                fullWidth
                                                InputProps={{
                                                    endAdornment: (
                                                        <IconButton
                                                            disabled={submitting}
                                                            color={'paper.secondary'}
                                                            onClick={() => {
                                                                form.change('partnerId', createUUId().toUpperCase())
                                                            }}
                                                        >
                                                            <AutoModeRounded className={"icon normal"}/>
                                                        </IconButton>
                                                    )
                                                }}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'name'}
                                                label={'Name'}
                                                required={required.name}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'phone'}
                                                label={'Phone'}
                                                required={required.phone}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'fax'}
                                                label={'Fax'}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'country'}
                                                label={'Country'}
                                                required={required.country}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'province'}
                                                label={'Province'}
                                                required={required.province}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'city'}
                                                label={'City'}
                                                required={required.city}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'postalCode'}
                                                label={'Postal Code'}
                                                required={required.postalCode}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <MuiRFF.TextField
                                                name={'fullAddress'}
                                                label={'Full Address'}
                                                required={required.fullAddress}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <MuiRFF.TextField
                                                name={'logo'}
                                                label={'Logo'}
                                                required={required.logo}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'adminId'}
                                                label={'Admin ID'}
                                                required={required.adminId}
                                                disabled={submitting}
                                                fullWidth
                                                InputProps={{
                                                    endAdornment: (
                                                        <IconButton
                                                            disabled={submitting}
                                                            color={'paper.secondary'}
                                                            onClick={() => {
                                                                form.change('adminId', createUUId().toUpperCase())
                                                            }}
                                                        >
                                                            <AutoModeRounded className={"icon normal"}/>
                                                        </IconButton>
                                                    )
                                                }}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'adminEmail'}
                                                label={'Admin Email'}
                                                required={required.adminEmail}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <MuiRFF.TextField
                                                name={'description'}
                                                label={'Description'}
                                                disabled={submitting}
                                                fullWidth
                                                multiline
                                                minRows={4}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'center'}}>
                                            <Button
                                                type={'submit'}
                                                size={'medium'}
                                                variant={'contained'}
                                                disabled={submitting}
                                                sx={{minWidth: 'min(300px, 80%)'}}
                                            >
                                                {submitting ? 'Creating...' : 'Create'}
                                            </Button>
                                        </Grid2>
                                    </Grid2>
                                )
                            }}
                        </Form>
                    </Grid2>
                </Grid2>
            </Card>
        </>

    );
}

export default PartnerManagementView;